import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  FitnessCenter as FitnessCenterIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as EmojiEventsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`athlete-tabpanel-${index}`}
      aria-labelledby={`athlete-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `athlete-tab-${index}`,
    'aria-controls': `athlete-tabpanel-${index}`,
  };
}

export default function AthleteProfilePage() {
  const { athleteId } = useParams<{ athleteId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [trainerNotes, setTrainerNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mock data - in production, this would come from RTK Query
  const athlete = {
    id: athleteId,
    name: 'John Athlete',
    email: 'john.athlete@example.com',
    consentStatus: 'approved',
    lastActivityDate: '2025-12-18T10:30:00Z',
    ctl: 85.5,
    atl: 92.3,
    tsb: -6.8,
    stravaConnected: true,
    joinedDate: '2025-01-15T00:00:00Z',
  };

  const recentActivities = [
    {
      id: '1',
      name: 'Morning Run',
      date: '2025-12-18T06:00:00Z',
      sportType: 'Run',
      distance: 10500,
      duration: 3600,
      tss: 65,
    },
    {
      id: '2',
      name: 'Evening Bike',
      date: '2025-12-17T17:00:00Z',
      sportType: 'Ride',
      distance: 45000,
      duration: 7200,
      tss: 120,
    },
  ];

  const assignedWorkouts = [
    {
      id: '1',
      name: 'Interval Training',
      scheduledDate: '2025-12-20T00:00:00Z',
      status: 'pending',
      priority: 'high',
      courseName: 'VO2 Max Intervals',
    },
    {
      id: '2',
      name: 'Recovery Run',
      scheduledDate: '2025-12-19T00:00:00Z',
      status: 'completed',
      priority: 'low',
      courseName: 'Easy Recovery',
    },
  ];

  const personalRecords = [
    { distance: '5K', time: '18:45', date: '2025-11-15' },
    { distance: '10K', time: '39:20', date: '2025-10-22' },
    { distance: 'Half Marathon', time: '1:28:15', date: '2025-09-10' },
  ];

  const zones = {
    heartRate: [
      { zone: 1, min: 120, max: 140, description: 'Recovery' },
      { zone: 2, min: 140, max: 155, description: 'Endurance' },
      { zone: 3, min: 155, max: 170, description: 'Tempo' },
      { zone: 4, min: 170, max: 185, description: 'Threshold' },
      { zone: 5, min: 185, max: 200, description: 'VO2 Max' },
    ],
    pace: [
      { zone: 1, min: '6:00', max: '5:30', description: 'Easy' },
      { zone: 2, min: '5:30', max: '5:00', description: 'Moderate' },
      { zone: 3, min: '5:00', max: '4:30', description: 'Tempo' },
      { zone: 4, min: '4:30', max: '4:00', description: 'Threshold' },
      { zone: 5, min: '4:00', max: '3:30', description: 'Speed' },
    ],
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSaveNotes = async () => {
    setLoading(true);
    try {
      // In production, this would call the API via RTK Query
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsEditingNotes(false);
      setError(null);
    } catch (err) {
      setError('Failed to save notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingNotes(false);
    setTrainerNotes('');
  };

  const formatDistance = (meters: number) => {
    return `${(meters / 1000).toFixed(2)} km`;
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'revoked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!athlete) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Athlete not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Athlete Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" gutterBottom>
                  {athlete.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {athlete.email}
                  </Typography>
                </Box>
                <Chip
                  icon={
                    athlete.consentStatus === 'approved' ? (
                      <CheckCircleIcon />
                    ) : (
                      <CancelIcon />
                    )
                  }
                  label={`Consent: ${athlete.consentStatus}`}
                  color={getStatusColor(athlete.consentStatus)}
                  size="small"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h5" color="primary">
                      {athlete.ctl.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      CTL
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h5" color="secondary">
                      {athlete.atl.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ATL
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography
                      variant="h5"
                      color={athlete.tsb < 0 ? 'error' : 'success.main'}
                    >
                      {athlete.tsb.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      TSB
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Consent Check */}
      {athlete.consentStatus !== 'approved' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This athlete has not approved data sharing. Analytics data is limited.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={2}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="athlete profile tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<TrendingUpIcon />} label="Overview" {...a11yProps(0)} />
          <Tab icon={<FitnessCenterIcon />} label="Activities" {...a11yProps(1)} />
          <Tab icon={<AssignmentIcon />} label="Workouts" {...a11yProps(2)} />
          <Tab icon={<EmojiEventsIcon />} label="Records & Zones" {...a11yProps(3)} />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Athlete Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Joined:</strong>{' '}
                      {format(parseISO(athlete.joinedDate), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Last Activity:</strong>{' '}
                      {format(parseISO(athlete.lastActivityDate), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Strava Connected:</strong>{' '}
                      {athlete.stravaConnected ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Trainer Notes</Typography>
                    {!isEditingNotes ? (
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingNotes(true)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    ) : (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={handleSaveNotes}
                          color="primary"
                          disabled={loading}
                        >
                          {loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={loading}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {isEditingNotes ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={trainerNotes}
                      onChange={(e) => setTrainerNotes(e.target.value)}
                      placeholder="Add notes about this athlete..."
                      disabled={loading}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {trainerNotes || 'No notes yet. Click edit to add notes.'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Activities Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Recent Activities
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Sport</TableCell>
                  <TableCell align="right">Distance</TableCell>
                  <TableCell align="right">Duration</TableCell>
                  <TableCell align="right">TSS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentActivities.map((activity) => (
                  <TableRow
                    key={activity.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/activities/${activity.id}`)}
                  >
                    <TableCell>{activity.name}</TableCell>
                    <TableCell>
                      {format(parseISO(activity.date), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>{activity.sportType}</TableCell>
                    <TableCell align="right">
                      {formatDistance(activity.distance)}
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(activity.duration)}
                    </TableCell>
                    <TableCell align="right">{activity.tss}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Workouts Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Assigned Workouts</Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/trainer/assign-workout?athleteId=${athleteId}`)}
            >
              Assign New Workout
            </Button>
          </Box>
          <Grid container spacing={2}>
            {assignedWorkouts.map((workout) => (
              <Grid item xs={12} md={6} key={workout.id}>
                <Card
                  sx={{
                    borderLeft: 4,
                    borderColor:
                      workout.status === 'completed' ? 'success.main' : 'warning.main',
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6">{workout.name}</Typography>
                      <Chip
                        label={workout.priority}
                        color={getPriorityColor(workout.priority)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {workout.courseName}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Scheduled:</strong>{' '}
                      {format(parseISO(workout.scheduledDate), 'MMM dd, yyyy')}
                    </Typography>
                    <Chip
                      label={workout.status}
                      color={workout.status === 'completed' ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Records & Zones Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Personal Records
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Distance</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {personalRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{record.distance}</TableCell>
                        <TableCell>{record.time}</TableCell>
                        <TableCell>{record.date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Heart Rate Zones
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zone</TableCell>
                      <TableCell>Range (bpm)</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zones.heartRate.map((zone) => (
                      <TableRow key={zone.zone}>
                        <TableCell>Z{zone.zone}</TableCell>
                        <TableCell>
                          {zone.min} - {zone.max}
                        </TableCell>
                        <TableCell>{zone.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Pace Zones
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zone</TableCell>
                      <TableCell>Range (min/km)</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zones.pace.map((zone) => (
                      <TableRow key={zone.zone}>
                        <TableCell>Z{zone.zone}</TableCell>
                        <TableCell>
                          {zone.max} - {zone.min}
                        </TableCell>
                        <TableCell>{zone.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
}